import { useState, useRef, useEffect } from 'react';
import { useUIContext } from '../../../context/UIContext';
import {
    CLIP_ORGANIZER_TOP_BOTTOM_MARGINS,
    DEFAULT_PLAYER_RATIO,
    INITIAL_CLIPS_LIST_PADDING,
    MAX_CARD_LIST_HEIGHT,
    MIN_CARD_LIST_HEIGHT,
    PADDING_COEFFICIENT
} from '../../../constants/editorConst';
import { throttle } from 'engage/utils/general';

export default function useEditorResize({
    playerClassName,
    miniSidebar,
    miniRightSidebar,
    videoSliderChangedFlag,
    pageContentRef
}) {
    const [clipsListStyles, setClipsListStyles] = useState({});
    const editorRef = useRef(null);
    const { changeForcedPlayerStyles, forceMinSlider, forcedMinSlider } = useUIContext();

    const updateEditorContainerSize = () => {
        const deviation = -12;
        if (window.innerHeight - pageContentRef?.current?.scrollHeight <= deviation) {
            // We should keep max width for the player which is editorRef?.current.offsetWidth
            // Calculate max height of player based on player component children (player + sliders)
            const childrenArray = Array.from(editorRef?.current?.children);
            const childrenMarginsSum = childrenArray.reduce((acc, children) => {
                const { marginTop, marginBottom } = window.getComputedStyle(children);

                return acc + parseFloat(marginTop) + parseFloat(marginBottom);
            }, 0);

            const childrenHeightSum = childrenArray
                .filter(item => !item.className?.includes(playerClassName))
                .reduce((acc, { offsetHeight }) => acc + offsetHeight, 0);

            // Calculate available height of player based on player component children (player + sliders)
            let maxPlayerHeight = Math.round(editorRef.current.offsetWidth / DEFAULT_PLAYER_RATIO);

            // Calculate clips list height with player max width
            let clipsListHeight =
                window.innerHeight - childrenMarginsSum - childrenHeightSum - CLIP_ORGANIZER_TOP_BOTTOM_MARGINS - maxPlayerHeight;

            // Early return if calculated list height > than MAX
            if (clipsListHeight > MAX_CARD_LIST_HEIGHT) {
                setClipsListStyles({ maxHeight: `${MAX_CARD_LIST_HEIGHT}px`, padding: `${INITIAL_CLIPS_LIST_PADDING}px 0` });
                changeForcedPlayerStyles({});
                return;
            }

            if (clipsListHeight < MIN_CARD_LIST_HEIGHT) {
                maxPlayerHeight =
                    window.innerHeight - MIN_CARD_LIST_HEIGHT - childrenMarginsSum - CLIP_ORGANIZER_TOP_BOTTOM_MARGINS - childrenHeightSum;
                clipsListHeight = MIN_CARD_LIST_HEIGHT;
            }

            if (maxPlayerHeight < window.innerHeight * 0.5) {
                videoSliderChangedFlag && forceMinSlider(true);
                return;
            }

            // Calculate player max-width based on player ration, that always stays same
            const maxWidth = `${Math.round(maxPlayerHeight * DEFAULT_PLAYER_RATIO)}px`;
            changeForcedPlayerStyles({ maxWidth });
            setClipsListStyles({
                maxHeight: `${clipsListHeight}px`,
                padding: `${Math.round(clipsListHeight / PADDING_COEFFICIENT)}px 0`,
                maxWidth,
            });
        } else {
            setClipsListStyles({ maxHeight: `${MAX_CARD_LIST_HEIGHT}px`, padding: `${INITIAL_CLIPS_LIST_PADDING}px 0` });
            changeForcedPlayerStyles({});
            forceMinSlider(false);
        }
    };

    useEffect(() => {
        if (!editorRef.current) {
            return;
        }

        const resizeObserver = new ResizeObserver(throttle(
        entries => {
                entries.forEach(() => {
                    updateEditorContainerSize();
                });
            }
        ), 150);

        resizeObserver.observe(editorRef.current);

        return () => resizeObserver.disconnect();
    }, [editorRef.current]);

    useEffect(() => {
        if (!editorRef?.current) {
            return;
        }

        setTimeout(updateEditorContainerSize, 100);
    }, [window.innerWidth, window.innerHeight, miniSidebar, miniRightSidebar, forcedMinSlider, videoSliderChangedFlag]);
    return {
        clipsListStyles, editorRef
    };
}
